// [PHASE-5 2026-10] Desktop-first notice for the school_admin and super_admin
// dashboards. Below DESKTOP_MIN_WIDTH it shows "This interface is designed for
// a computer" with a "Continue anyway" button. It is a notice and NEVER a
// block: one tap renders the dashboard, and the choice holds for the tab.
//
// The decision is taken ONCE, at mount. Narrowing the window later (a split
// screen, a rotated tablet, devtools) never swaps a mounted dashboard out for
// the notice: that unmounted it and lost every unsaved form. Widening the
// window while the notice is up does lift it, which unmounts nothing.
import React, { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Monitor } from 'lucide-react';
import {
  DESKTOP_MIN_WIDTH,
  shouldShowDesktopNotice,
  readDesktopNoticeDismissed,
  rememberDesktopNoticeDismissed,
} from '../lib/desktopGate';
import { safeStorage } from '../lib/safeStorage';

const currentWidth = () => (typeof window !== 'undefined' ? window.innerWidth : 0);

const DesktopFirstGate = ({ children }) => {
  const { t, i18n } = useTranslation();
  const [showNotice, setShowNotice] = useState(
    () => shouldShowDesktopNotice(currentWidth(), readDesktopNoticeDismissed(safeStorage())),
  );

  useEffect(() => {
    if (!showNotice) return undefined;   // the dashboard is mounted: never revisit
    const onResize = () => {
      if (currentWidth() >= DESKTOP_MIN_WIDTH) setShowNotice(false);
    };
    window.addEventListener('resize', onResize);
    return () => window.removeEventListener('resize', onResize);
  }, [showNotice]);

  if (!showNotice) return children;

  const continueAnyway = () => {
    rememberDesktopNoticeDismissed(safeStorage());
    setShowNotice(false);
  };

  return (
    <div
      dir={i18n.language === 'ar' ? 'rtl' : 'ltr'}
      role="dialog"
      aria-labelledby="desktop-notice-title"
      className="min-h-screen flex items-center justify-center p-4 bg-slate-50 dark:bg-gray-950"
    >
      <div className="w-full max-w-md bg-white dark:bg-gray-900 rounded-3xl shadow-lg border border-slate-200 dark:border-gray-800 p-8 text-center">
        <div className="w-16 h-16 rounded-2xl bg-teal-50 dark:bg-teal-900/40 flex items-center justify-center mx-auto mb-5">
          <Monitor className="w-8 h-8 text-teal-700 dark:text-teal-300" aria-hidden="true" />
        </div>
        <h1 id="desktop-notice-title" className="text-xl font-bold text-slate-900 dark:text-gray-100 mb-3">
          {t('desktop_notice_title')}
        </h1>
        <p className="text-sm leading-relaxed text-slate-600 dark:text-gray-300 mb-7">
          {t('desktop_notice_body')}
        </p>
        <button
          type="button"
          onClick={continueAnyway}
          className="w-full rounded-xl bg-teal-700 hover:bg-teal-800 text-white font-semibold text-sm px-4 py-3"
        >
          {t('desktop_notice_continue')}
        </button>
      </div>
    </div>
  );
};

export default DesktopFirstGate;
